// session.js — the anonymous login bootstrap, entirely client-side: persist the EBA device key in
// IndexedDB, self-sign a short session token, publish it as the coglative_session_capability
// cookie. No server round-trip, no password — the device key IS the identity and its derived GUID
// is stable across reloads. The private key is non-extractable, so XSS cannot exfiltrate it;
// losing the device orphans the account (the stated EBA model).

import { deviceIdentity, generateDeviceKey, signAnonToken } from "./eba.js";

const DB_NAME = "coglative-eba";
const STORE = "device-key";
const KEY_ID = "anon-device-key";
export const COOKIE_NAME = "coglative_session_capability";
// The DEVICE handle cookie, distinct from the session bearer: ALWAYS this device's own self-signed
// token, even when the bearer is a HUMAN cap-token. The server keys the device CELL on its GUID, so
// two devices on ONE account get SEPARATE cells (#131) while principalUserUri keeps them in one room.
export const DEVICE_COOKIE_NAME = "coglative_device_capability";
const TTL_SEC = 3600; // token validity
const REFRESH_MARGIN_SEC = 300; // re-sign this long before expiry

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db, key, val) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function loadOrCreateDeviceKey() {
  const db = await openDb();
  const stored = await idbGet(db, KEY_ID);
  if (stored && stored.privateKey && stored.publicKey) {
    return await deviceIdentity({
      privateKey: stored.privateKey,
      publicKey: stored.publicKey,
    });
  }
  const id = await generateDeviceKey();
  await idbPut(db, KEY_ID, {
    privateKey: id.keyPair.privateKey,
    publicKey: id.keyPair.publicKey,
  });
  return id;
}

// READ-ONLY, unlike loadOrCreateDeviceKey: asking cannot manufacture an affirmative answer. This
// is the durable "have we been here before" fact — the session COOKIE is not, and when it expired
// the chrome decided a returning visitor was brand new and showed the paywall.
export async function hasDeviceKey() {
  try {
    const db = await openDb();
    const stored = await idbGet(db, KEY_ID);
    return !!(stored && stored.privateKey && stored.publicKey);
  } catch (_) {
    // Storage unavailable (private mode, evicted origin): we cannot prove a prior visit. Fail
    // toward the gate — showing it to a returning user is recoverable, failing to boot is not.
    return false;
  }
}

// The Set-Cookie value publishing the token. Apex `Domain` so it attaches to the mesh host, Secure +
// SameSite=None so it rides the cross-origin CONNECT, NOT HttpOnly (the chrome refreshes it).
export function sessionCookie(token, domain) {
  const d = domain ? "; Domain=" + domain : "";
  return (
    COOKIE_NAME + "=" + token + "; Path=/" + d +
    "; Secure; SameSite=None; Max-Age=" + TTL_SEC
  );
}

// NOTE: the WT API attaches NO cookies to the H3 CONNECT, so this is a same-origin-fetch fallback;
// the token's real vehicle is the X-Coglative-Device preflight header.
export function deviceCookie(token, domain) {
  const d = domain ? "; Domain=" + domain : "";
  return (
    DEVICE_COOKIE_NAME + "=" + token + "; Path=/" + d +
    "; Secure; SameSite=None; Max-Age=" + TTL_SEC
  );
}

let current = null; // { guid, exp, token } -- in-memory cache to avoid re-signing per call

// The current self-signed anon token, or ''. The wt-bind preflight presents it as
// `Authorization: Bearer` — the server's WtBind handler reads the cap-token from that header ONLY
// (no cookie path), exactly as the iOS chrome sets SubstrateClient.bearerToken.
export function currentAnonToken() {
  return current ? current.token : "";
}

// Ensure a fresh anon session: (re)sign when absent or within REFRESH_MARGIN of expiry, publish the
// cookie, expose the token, return the stable GUID. Idempotent and cheap.
export async function ensureSession(nowSec, cookieDomain) {
  const now = nowSec || Math.floor(Date.now() / 1000);
  if (current && current.exp - now > REFRESH_MARGIN_SEC) {
    // Still valid — but ALWAYS re-publish the window token: bridge-op's dial reads it directly, and
    // a stale/cleared value on a long-lived tab would dial with no bearer.
    if (typeof window !== "undefined") window.__coglativeAnonToken = current.token;
    return current.guid;
  }
  const id = await loadOrCreateDeviceKey();
  const token = await signAnonToken(id, now, TTL_SEC);
  document.cookie = sessionCookie(token, cookieDomain);
  current = { guid: id.guid, exp: now + TTL_SEC, token };
  // On window so bridge-op.js (a non-module IIFE) can read it without an import cycle.
  if (typeof window !== "undefined") window.__coglativeAnonToken = token;
  return id.guid;
}

// Sign in from the login banner: only a revoked anon subject gets a new key (models/logout_reentry.qnt).
export function reloginAction(reason, identified) {
  if (reason === "passkey_required" || identified) return "passkey";
  return reason === "revoked" ? "rotate" : "resign";
}

// Re-sign with the SAME device key, dropping the cached token: a refusal that is not about the subject.
export async function resignSession(nowSec, cookieDomain) {
  current = null;
  return ensureSession(nowSec, cookieDomain);
}

// Called on EVERY dial, anon OR identified: the device handle is independent of the bearer, so an
// identified session still needs its cell keyed on THIS device.
let currentDevice = null; // { exp, token } -- cache, separate from `current`
export async function ensureDeviceCookie(nowSec, cookieDomain) {
  const now = nowSec || Math.floor(Date.now() / 1000);
  if (currentDevice && currentDevice.exp - now > REFRESH_MARGIN_SEC) {
    if (typeof document !== "undefined") {
      document.cookie = deviceCookie(currentDevice.token, cookieDomain);
    }
    if (typeof window !== "undefined") window.__coglativeDeviceToken = currentDevice.token;
    return currentDevice.token;
  }
  const id = await loadOrCreateDeviceKey();
  const token = await signAnonToken(id, now, TTL_SEC);
  if (typeof document !== "undefined") {
    document.cookie = deviceCookie(token, cookieDomain);
  }
  currentDevice = { exp: now + TTL_SEC, token };
  // bridge-op attaches this as X-Coglative-Device on the preflight — the LOAD-BEARING vehicle, since
  // the CONNECT carries only the nonce. That proven GUID keys THIS device's own live cell (#131).
  if (typeof window !== "undefined") window.__coglativeDeviceToken = token;
  return token;
}

// deleteSession — "log out = delete" for an unidentified user (SPEC-identity-model-v0): the device
// key IS the identity, so logging out DESTROYS it. The next ensureSession() mints a brand-new
// GUID; the old one's server-side data is orphaned for the reaper (task #166 signals it sooner).
export async function deleteSession(cookieDomain) {
  try {
    const db = await openDb();
    await idbDelete(db, KEY_ID);
  } catch (e) {
    // eslint-disable-next-line no-console
    if (typeof console !== "undefined") console.warn("[session] device-key delete:", e && e.message ? e.message : e);
  }
  const d = cookieDomain ? "; Domain=" + cookieDomain : "";
  if (typeof document !== "undefined") {
    document.cookie = COOKIE_NAME + "=; Path=/" + d + "; Secure; SameSite=None; Max-Age=0";
    document.cookie = DEVICE_COOKIE_NAME + "=; Path=/" + d + "; Secure; SameSite=None; Max-Age=0";
  }
  current = null;
  currentDevice = null;
  if (typeof window !== "undefined") {
    window.__coglativeAnonToken = "";
    window.__coglativeDeviceToken = "";
  }
}
